import { z } from 'zod';

import { ApiError } from '@/api/ApiError';
import { request } from '@/api/httpClient';
import type { ManagerContext } from '@/features/auth/types/ManagerContext';

const managerContextSchema = z.object({
  readings: z.array(z.unknown()),
  dosages: z.array(z.unknown()),
});

const userSchema = z.object({
  id: z.number().int().positive(),
  name: z.string().min(1),
  email: z.email(),
  organisation_id: z.number().int().positive(),
});

export async function getManagerContext(): Promise<ManagerContext> {
  // A Laravel web GET refreshes Passport's short-lived API cookie. This also
  // turns a missing web session into a clear 401 before calling the API.
  const session = await fetch('/admin-session', {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (!session.ok) {
    throw new ApiError('Manager sign in required.', session.status);
  }

  const [userResponse, managerResponse] = await Promise.all([
    request<unknown>('/user'),
    request<unknown>('/managers/context'),
  ]);
  const user = userSchema.parse(userResponse);
  managerContextSchema.parse(managerResponse);

  return {
    user: {
      id: user.id,
      name: user.name,
      email: user.email,
      initials: getInitials(user.name),
      // The existing API does not expose permissions. API responses remain authoritative.
      permissions: null,
    },
    organisation: { id: user.organisation_id },
  };
}

function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}
